import React, { useState } from 'react';
import { Eye, Edit2, Trash2, User } from 'lucide-react';
import { getEmployeePhotoUrl } from '../../../utils/imageUtils';
import Pagination from '../../common/Pagination';

// Badge de tipo de contrato/nómina
function TipoBadge({ emp }) {
  const tipo = emp.tipoNomina || emp.tipoEmpleado || emp.tipoContrato;
  if (!tipo) return <span className="text-xs text-gray-300">—</span>;

  const map = {
    mensual:     { label: 'Mensual',    cls: 'bg-blue-50 text-blue-700' },
    quincenal:   { label: 'Quincenal',  cls: 'bg-indigo-50 text-indigo-700' },
    indefinido:  { label: 'Indefinido', cls: 'bg-green-50 text-green-700' },
    plazo_fijo:  { label: 'Plazo Fijo', cls: 'bg-amber-50 text-amber-700' },
    temporal:    { label: 'Temporal',   cls: 'bg-orange-50 text-orange-700' },
    prueba:      { label: 'En Prueba',  cls: 'bg-gray-100 text-gray-600' },
    por_obra:    { label: 'Por Obra',   cls: 'bg-gray-100 text-gray-700' },
  };
  const cfg = map[tipo] || { label: tipo, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function EmployeeTable({ employees, onEdit, onDelete, onViewDetails }) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const sorted = [...employees].sort((a, b) => {
    const idA = a.employeeId || a.employee_id || '';
    const idB = b.employeeId || b.employee_id || '';
    return idA.localeCompare(idB);
  });

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const start      = (currentPage - 1) * itemsPerPage;
  const current    = sorted.slice(start, start + itemsPerPage);

  const getFotoUrl = getEmployeePhotoUrl;

  if (sorted.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
        <User size={40} className="text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500">No hay empleados para mostrar</p>
        <p className="text-xs text-gray-400 mt-1">Ajusta los filtros o agrega nuevos empleados</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">

          {/* ── Head ──────────────────────────────────────────────── */}
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-10 px-4 py-3">
                <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" disabled />
              </th>
              {['EMPLEADO', 'ID', 'DEPARTAMENTO', 'CARGO', 'CONTACTO', 'SALARIO', 'ESTADO', 'TIPO', ''].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ──────────────────────────────────────────────── */}
          <tbody className="divide-y divide-gray-100">
            {current.map((emp) => {
              const initials = `${emp.nombre?.[0] || ''}${emp.apellido?.[0] || ''}`.toUpperCase();
              const avatarColors = [
                'bg-blue-100 text-blue-700',
                'bg-green-100 text-green-700',
                'bg-amber-100 text-amber-700',
                'bg-orange-100 text-orange-700',
                'bg-red-100 text-red-700',
                'bg-slate-100 text-slate-600',
              ];
              const colorIdx = (emp.nombre?.charCodeAt(0) || 0) % avatarColors.length;
              const avatarColor = avatarColors[colorIdx];

              return (
                <tr
                  key={emp.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer group"
                  onClick={() => onViewDetails(emp)}
                >
                  {/* Checkbox */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
                  </td>

                  {/* Empleado */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {emp.foto ? (
                          <img
                            src={getFotoUrl(emp.foto)}
                            alt={emp.nombre}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${avatarColor}`}
                          style={{ display: emp.foto ? 'none' : 'flex' }}
                        >
                          {initials}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {emp.nombre} {emp.apellido}
                        </p>
                        {emp.cedula && (
                          <p className="text-xs text-gray-400">{emp.cedula}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* ID */}
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-blue-600">
                      {emp.employeeId || emp.employee_id}
                    </span>
                  </td>

                  {/* Departamento */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {emp.departamento || emp.departamento_nombre || '—'}
                    </span>
                  </td>

                  {/* Cargo */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {emp.cargo || emp.cargo_nombre || '—'}
                    </span>
                  </td>

                  {/* Contacto */}
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-600">{emp.celular || emp.telefono || '—'}</p>
                    {emp.email && (
                      <p className="text-xs text-gray-400 truncate max-w-[140px]">{emp.email}</p>
                    )}
                  </td>

                  {/* Salario */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {emp.salario
                        ? `RD$ ${parseFloat(emp.salario).toLocaleString('es-DO', { minimumFractionDigits: 0 })}`
                        : '—'}
                    </span>
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${
                      emp.estado === 'activo'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-600'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${emp.estado === 'activo' ? 'bg-green-500' : 'bg-red-400'}`} />
                      {emp.estado === 'activo' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>

                  {/* Tipo */}
                  <td className="px-4 py-3">
                    <TipoBadge emp={emp} />
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onViewDetails(emp)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Ver expediente"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => onEdit(emp)}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`¿Desactivar a ${emp.nombre} ${emp.apellido}?`)) onDelete(emp.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        total={sorted.length}
        pageSize={itemsPerPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
